import React, { useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { Link, withRouter } from 'react-router-dom'

import DNPTable from '../components/DNPTable'
import FAIcon from '../components/FAIcon'
import CasesService from '../services/CasesService'
import format from '../services/format'
import GeneralTasksService from '../services/GeneralTasksService'
import TasksService from '../services/TasksService'
import Permissions, { CAPABILITIES } from '../utils/Permissions'

import './TasksListPage.scss'

const TasksListPage = withRouter(({ history, user }) => {
  const [allTasks, setAllTasks] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('open')
  const [deletedIds, setDeletedIds] = useState(new Set())
  const [deleteError, setDeleteError] = useState('')
  const [forceWarning, setForceWarning] = useState(null) // { task, message }
  const [loadError, setLoadError] = useState('')

  const canDelete = Permissions.can(user, CAPABILITIES.DELETE_TASK)

  useEffect(() => {
    setLoadError('')
    Promise.all([
      TasksService.getAll(false),
      GeneralTasksService.getMyTasks(),
      CasesService.getAll(['caseNumber', 'client.firstName', 'client.lastName']),
    ])
      .then(([caseTasks, generalTasks, cases]) => {
        const caseMap = {}
        cases.forEach(c => {
          caseMap[c.caseNumber] = c.client ? `${c.client.firstName} ${c.client.lastName}` : null
        })

        const normalized = [
          ...caseTasks.map(t => ({
            id: t.taskNumber || t.TaskNumber,
            title: t.title || t.Title,
            dueDateUtc: t.dueDateUtc || t.DueDateUtc,
            completedDateUtc: t.completeDateTimeUtc || t.CompleteDateTimeUtc || null,
            caseNumber: t.caseNumber || t.CaseNumber,
            clientName: caseMap[t.caseNumber || t.CaseNumber] || `Case ${t.caseNumber || t.CaseNumber}`,
            assignedToUserName: t.assignedToUserName || t.AssignedToUserName || null,
            completed: !!(t.complete || t.Complete),
            isGeneralTask: false,
            link: `/cases/${t.caseNumber || t.CaseNumber}/tasks/${t.taskNumber || t.TaskNumber}`,
          })),
          ...generalTasks.map(t => ({
            id: t.id || t.Id,
            title: t.title || t.Title,
            dueDateUtc: t.dueDateUtc || t.DueDateUtc,
            completedDateUtc: t.completedDateUtc || t.CompletedDateUtc || null,
            caseNumber: null,
            clientName: null,
            assignedToUserName: t.assignedToUserName || null,
            completed: !!(t.complete || t.Complete),
            isGeneralTask: true,
            link: `/tasks/${t.id || t.Id}`,
          })),
        ]

        // Sort open by due date asc; completed by completedDate desc
        normalized.sort((a, b) => {
          if (!a.completed && !b.completed) {
            return (a.dueDateUtc || '') < (b.dueDateUtc || '') ? -1 : 1
          }
          return (b.completedDateUtc || '') < (a.completedDateUtc || '') ? -1 : 1
        })

        setAllTasks(normalized)
      })
      .catch(err => {
        setLoadError(err.message || 'Failed to retrieve tasks.')
      })
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDeleteClick = async (e, task) => {
    e.preventDefault()
    e.stopPropagation()
    setDeleteError('')
    try {
      if (task.isGeneralTask) {
        await GeneralTasksService.delete(task.id)
      } else {
        await TasksService.deleteTask(task.caseNumber, task.id)
      }
      setDeletedIds(prev => new Set([...prev, task.id]))
    } catch (err) {
      if (err.requiresForce) {
        setForceWarning({ task, message: err.serverMessage })
      } else {
        setDeleteError(err.message || 'Failed to delete task.')
      }
    }
  }

  const handleForceDelete = async () => {
    if (!forceWarning) return
    setDeleteError('')
    try {
      await TasksService.deleteTask(forceWarning.task.caseNumber, forceWarning.task.id, true)
      setDeletedIds(prev => new Set([...prev, forceWarning.task.id]))
      setForceWarning(null)
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete task.')
      setForceWarning(null)
    }
  }

  const openTasks = allTasks.filter(t => !t.completed && !deletedIds.has(t.id))
  const completedTasks = allTasks.filter(t => t.completed && !deletedIds.has(t.id))
  const displayedTasks = activeTab === 'open' ? openTasks : completedTasks

  const completedOnColumn = {
    title: 'Completed On',
    getText: t => (t.completedDateUtc ? format.date(t.completedDateUtc) : <>&mdash;</>),
  }

  const deleteColumn = {
    title: '',
    getText: t => (
      <button
        type="button"
        className="btn btn-link btn-sm p-0"
        style={{ color: '#dc3545' }}
        title="Delete this task"
        onClick={e => handleDeleteClick(e, t)}
      >
        <FAIcon name="trash-alt" type="far fa-md" />
      </button>
    ),
  }

  return (
    <div className="container mt-4 tasks-list-page">
      <Helmet title="Tasks" />

      {forceWarning && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Task</h5>
              </div>
              <div className="modal-body">
                <p>{forceWarning.message}</p>
                <p>Are you sure you want to permanently delete this task and all associated data?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setForceWarning(null)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleForceDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <ul className="nav nav-tabs tasks-tabs">
          <li className="nav-item">
            <button className={`nav-link ${activeTab === 'open' ? 'active' : ''}`} onClick={() => setActiveTab('open')}>
              Open
              {!isLoading && <span className="badge badge-pill badge-secondary ml-2">{openTasks.length}</span>}
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveTab('completed')}
            >
              Completed
              {!isLoading && <span className="badge badge-pill badge-secondary ml-2">{completedTasks.length}</span>}
            </button>
          </li>
        </ul>
        <button className="btn btn-primary btn-sm" onClick={() => history.push('/tasks/new')}>
          <FAIcon name="plus" className="mr-1" />
          New Task
        </button>
      </div>

      {!!deleteError && <div className="alert alert-danger py-2">{deleteError}</div>}
      {!!loadError && <div className="alert alert-danger py-2">{loadError}</div>}

      {isLoading ? (
        <div className="d-flex justify-content-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <DNPTable
          items={displayedTasks}
          columns={[
            {
              title: 'Task',
              getText: t => <Link to={t.link}>{t.title}</Link>,
            },
            {
              title: 'Case',
              getText: t =>
                t.clientName ? (
                  <Link to={`/cases/${t.caseNumber}/tasks`} className="text-muted">
                    {t.clientName}
                  </Link>
                ) : (
                  <span className="text-muted font-italic">General</span>
                ),
            },
            {
              title: 'Due',
              getText: t => (t.dueDateUtc ? format.date(t.dueDateUtc) : <>&mdash;</>),
            },
            {
              title: 'Assigned To',
              getText: t => t.assignedToUserName || <span className="text-muted">Unassigned</span>,
            },
            ...(activeTab === 'completed' ? [completedOnColumn] : []),
            ...(canDelete && activeTab === 'open' ? [deleteColumn] : []),
          ]}
          noItemsText={activeTab === 'open' ? 'No open tasks' : 'No completed tasks'}
        />
      )}
    </div>
  )
})

export default TasksListPage
